import {
  ADVERT_FORMAT_TYPES,
  ADVERT_ITINERARY_SEARCH_TYPES,
  FLIGHTS_INLINE_TYPES,
  SERVICE,
} from '@web-platform/shared-universal-constants/src/adverts';
import { generateAdvertIdFromStrevdaSlot } from '@web-platform/shared-universal-utils-frontend/src/helpers/generateAdvertId';

import type { FlightsInlineFields } from '@skyscanner/strevda-gam';
import type { Slot } from '@web-platform/shared-universal-types/src/network/endpoints/strevda';
import type {
  Advert,
  AnalyticsProperties,
  CreativeFlightsInline,
  CreativeFlightsInlineItinerary,
  CreativeFlightsPricedBrandInline,
  CreativeFlightsUnpricedBrandInline,
  LayoutTypeItinerary,
  Selection,
} from '@web-platform/shared-universal-types/src/redux/acorn/state/flightsDayView/entitiesSearchResults/adverts/types';
import type { ID } from '@web-platform/shared-universal-types-utils/src/utils';

const getFlightsInlineCreativeFromSlotParams = (slot: Slot): CreativeFlightsInline => {
  const { advert, position } = slot;
  const {
    clickthroughUrl: redirectUrl,
    deeplink,
    layoutType,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    // set by strevda-runtime
    removedeeplinkids,
    // set by DFP
    search,
    title,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  } = advert.fields as FlightsInlineFields;

  // Creatives from strevda-runtime (via DFP) will always be of type CreativeFlightsInline
  // Default values are set because DFP will not provide them
  const slotFields = {
    appIndex: position.toString(),
    callToActionText: null,
    ctaButtonTitleEnabled: true,
    deeplink,
    deeplinkParams: null,
    itineraryId: null,
    message,
    partner,
    partnerDesktopLogo,
    partnerLogo,
    // DFP ads are out of DSA scope — pioPartnerName not provided by DFP
    pioPartnerName: '',
    redirectUrl,
    removeDeeplinkIds: (removedeeplinkids || '').toLowerCase() === 'true',
    requestedDepth: null,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    selection: search as Selection,
    title,
  };

  if (layoutType === FLIGHTS_INLINE_TYPES.BRAND) {
    if (search === ADVERT_ITINERARY_SEARCH_TYPES.UNPRICED) {
      const creative: CreativeFlightsUnpricedBrandInline = {
        appIndex: slotFields.appIndex,
        callToActionText: slotFields.callToActionText,
        ctaButtonTitleEnabled: slotFields.ctaButtonTitleEnabled,
        format: ADVERT_FORMAT_TYPES.UNPRICED_BRAND_INLINE,
        message: slotFields.message,
        partnerDesktopLogo: slotFields.partnerDesktopLogo,
        partnerLogo: slotFields.partnerLogo,
        partner: slotFields.partner,
        pioPartnerName: slotFields.pioPartnerName,
        redirectUrl: slotFields.redirectUrl,
        title: slotFields.title,
      };

      return creative;
    }

    const creative: CreativeFlightsPricedBrandInline = {
      ...slotFields,
      format: ADVERT_FORMAT_TYPES.PRICED_BRAND_INLINE,
    };

    return creative;
  }

  const creative: CreativeFlightsInlineItinerary = {
    ...slotFields,
    format: ADVERT_FORMAT_TYPES.INLINE,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    layoutType: layoutType as LayoutTypeItinerary,
  };

  return creative;
};

export default function transformStrevdaSlotToAdvert(slot: Slot): Advert {
  const { advert, position } = slot;
  const { trackingContext, trackingPixels } = advert;
  const creative: CreativeFlightsInline = getFlightsInlineCreativeFromSlotParams(slot);
  const analyticsProperties: AnalyticsProperties = {
    ...trackingContext,
    priority: 'UNSET_PRIORITY_TYPE',
    service: SERVICE.DFP,
  };

  const id: ID = generateAdvertIdFromStrevdaSlot({
    advert,
    position,
  });

  return {
    id,
    position,
    creative,
    analyticsProperties,
    pixels: trackingPixels,
  };
}
